import { useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { EffectiveAccessTab } from './EffectiveAccessTab';
import { ManualAccessTab } from './ManualAccessTab';
import { RoleAccessTab } from './RoleAccessTab';

type Tab = 'role' | 'manual' | 'effective';

const tabs: { id: Tab; label: string }[] = [
  { id: 'role', label: 'Role access' },
  { id: 'manual', label: 'Manual access' },
  { id: 'effective', label: 'Effective access' },
];

/**
 * Settings › Branch relationships — who may see which branches, per module
 * (migration 128; Data-Model §17; insights SPEC-shared-finance-module §2b).
 *
 * Admin maintains Role access and Manual access; helpdesk sees all three read
 * only and lands on Effective access, which answers "why can't I see branch
 * X?". The API enforces the same split.
 */
export function BranchRelationshipsPage() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState<Tab>(isAdmin ? 'role' : 'effective');

  return (
    <section className="max-w-6xl">
      <header className="mb-4">
        <h1 className="font-heading text-2xl font-bold text-navy">Branch relationships</h1>
        <p className="mt-1 text-sm text-darkGrey">
          Which branches each person can see in each module. Access only ever widens: a role or a manual row adds
          branches, and no row means no access. The app role in each tenant still decides whether they may use the
          module at all.
        </p>
        {!isAdmin && <p className="mt-1 text-xs text-darkGrey/70">Read only — changes are made by an Oscar admin.</p>}
      </header>

      <div className="inline-flex rounded border border-border bg-white p-0.5 text-sm" role="tablist" aria-label="Branch relationships">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={tab === t.id ? 'rounded bg-navy px-3 py-1 font-medium text-white' : 'rounded px-3 py-1 font-medium text-darkGrey/70 hover:text-navy'}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-4" role="tabpanel">
        {tab === 'role' && <RoleAccessTab />}
        {tab === 'manual' && <ManualAccessTab />}
        {tab === 'effective' && <EffectiveAccessTab />}
      </div>
    </section>
  );
}
